import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  InputAdornment
} from '@mui/material';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import MissionPDF from './MissionPDF';
import { PDFViewer } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import { getNumberingSettings } from '../services/settings';

const MissionForm = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [wilayas, setWilayas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [transports, setTransports] = useState([]);
  const [selectedWilaya, setSelectedWilaya] = useState(''); // Sera mis à jour avec Blida
  const [selectedDaira, setSelectedDaira] = useState('Boufarik'); // Champ libre avec valeur par défaut
  const [selectedPays, setSelectedPays] = useState(4); // Default Algeria
  const [paysList] = useState([
    { id: 4, name: 'Algérie' },
    { id: 1, name: 'France' },
    { id: 2, name: 'Canada' },
    { id: 3, name: 'Suisse' },
    { id: 5, name: 'USA' },
    { id: 6, name: 'Espagne' },
    { id: 7, name: 'Portugal' }
  ]); // Static list of countries
  const [algeriaWilayas, setAlgeriaWilayas] = useState([]);
  const [confirmationData, setConfirmationData] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMissionCreated, setIsMissionCreated] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [communes, setCommunes] = useState([]);
  const [nextOrderNumber, setNextOrderNumber] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const formik = useFormik({
    initialValues: {
      employeeId: employee?.id ? employee.id : '',
      destinations: [],
      transportId: '',
      validityFrom: new Date().toISOString().split('T')[0],
      validityTo: '',
      reason: '',
      billet: ''
    },
    validationSchema: Yup.object({
      employeeId: Yup.number().required('Champ requis.'),
      destinations: Yup.array().min(1, 'Au moins une destination est requise.'),
      transportId: Yup.number().required('Champ requis.'),
      validityFrom: Yup.date()
        .min(new Date(new Date().setHours(0, 0, 0, 0)), 'La date ne peut pas être dans le passé')
        .required('Champ requis.'),
      validityTo: Yup.date()
        .required('Champ requis.')
        .min(Yup.ref('validityFrom'), 'La date de fin doit être après la date de début.'),
      reason: Yup.string().required('Champ requis.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setConfirmationData(values);
      setSubmitting(false);
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = await fetchCurrentUser();
        if (currentUser) {
          const roleId = Number(currentUser?.role_id ?? currentUser?.role);
          setUserRole(roleId);
          setEmployee(currentUser.employee);
          if (roleId !== 1 && roleId !== 5 && currentUser.employee?.id) {
            formik.setFieldValue('employeeId', currentUser.employee.id);
          }
        }

        // Fetch next order number
        try {
          const numberingResponse = await getNumberingSettings();
          if (numberingResponse.data && numberingResponse.data.nextMissionOrderNumber) {
            const currentYear = new Date().getFullYear();
            const formattedNumber = String(numberingResponse.data.nextMissionOrderNumber).padStart(4, '0') + '/' + currentYear;
            setNextOrderNumber(formattedNumber);
          }
        } catch (error) {
          // Fallback: generate default format
          const currentYear = new Date().getFullYear();
          setNextOrderNumber('0001/' + currentYear);
        }

        // Fetch public data first
        const [wilayasResponse, transportsResponse] = await Promise.all([
          api.get('/geography/wilayas?pays_id=4'),
          api.get('/transports')
        ]);
        
        // Trier les wilayas par nom
        const sortedWilayas = wilayasResponse.data.sort((a, b) => a.name.localeCompare(b.name));
        setWilayas(sortedWilayas);
        setTransports(transportsResponse.data);
        setAlgeriaWilayas(sortedWilayas);
        
        // Définir les valeurs par défaut: Blida (wilaya 9)
        setSelectedWilaya(9); // Blida
        
        // Only fetch employees if user is admin or admin-tenes
        const roleId = Number(currentUser?.role_id ?? currentUser?.role);
        if (roleId === 1) {
          // ADMIN: afficher tous les employés
          try {
            const employeesResponse = await api.get('/employees');
            // Trier les employés par nom (first_name puis last_name)
            const sortedEmployees = employeesResponse.data.sort((a, b) => {
              const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
              const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setEmployees(sortedEmployees);
          } catch (error) {
            console.error('Error fetching employees:', error);
          }
        } else if (roleId === 5) {
          // ADMIN-TENES: afficher les employés du site TENES uniquement
          try {
            const employeesResponse = await api.get('/employees');
            // Trier les employés par nom (first_name puis last_name)
            const sortedEmployees = employeesResponse.data.sort((a, b) => {
              const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
              const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setEmployees(sortedEmployees);
          } catch (error) {
            console.error('Error fetching employees:', error);
          }
        } else {
          setEmployees([currentUser.employee]);
        }
        
        setLoading(false);
        setUserLoaded(true);
      } catch (error) {
        setLoading(false);
        setUserLoaded(true);
      }
    };
    fetchData();
  }, [open]); 

  useEffect(() => {
    if (selectedPays !== 4) {
      setSelectedWilaya('');
      setSelectedDaira('');
    }
  }, [selectedPays]);

  useEffect(() => {
    const fetchWilayas = async () => {
      try {
        const response = await api.get(`/geography/wilayas?pays_id=${selectedPays}`);
        // Trier les wilayas par nom
        const sortedWilayas = response.data.sort((a, b) => a.name.localeCompare(b.name));
        setWilayas(sortedWilayas);
      } catch (error) {
        console.error('Error fetching wilayas:', error);
      }
    };
    fetchWilayas();
  }, [selectedPays]);

  const handleConfirm = async () => {
    if (isCreating) {
      return; // Prevent multiple clicks
    }
    
    setIsCreating(true);
    
    try {
      // Ajouter les informations de départ aux données
      const missionData = {
        ...confirmationData,
        departurePaysId: selectedPays,
        departureWilayaId: selectedWilaya,
        departureDaira: selectedDaira
      };
      
      const response = await api.post('/mission-orders', missionData);
      
      if (response.status === 201) {
        setSubmittedData(confirmationData);
        setIsMissionCreated(true);
        setIsFormDisabled(true);
        formik.resetForm();
        navigate('/mission-orders');
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'ordre de mission:', error);
      if (error.response) {
        console.error('Response error:', error.response.data);
        
        // Handle duplicate mission error
        if (error.response.status === 409) {
          setErrorMessage('Une mission identique a déjà été créée récemment. Veuillez patienter.');
        } else {
          setErrorMessage('Erreur lors de la création de la mission. Veuillez réessayer.');
        }
      }
    } finally {
      setIsCreating(false);
      setConfirmationData(null);
    }
  };

  const handleCancel = () => {
    setConfirmationData(null);
    setErrorMessage(null);
  };

  useEffect(() => {
    formik.setFieldValue(
      'destinations',
      destinations
    );
  }, [destinations]);

  const handleDestinationChange = async (index, field, value) => {
    const updatedDestinations = [...destinations];
    const destination = updatedDestinations[index];

    if (field === 'pays') {
      destination.pays = value;
      destination.wilaya = '';
      destination.daira = '';
      destination.ville = '';
    } else if (field === 'wilaya') {
      destination.wilaya = value;
      destination.daira = ''; // Réinitialiser la daira quand la wilaya change
    } else if (field === 'daira') {
      destination.daira = value; // daira est maintenant un champ libre
    } else if (field === 'ville') {
      destination.ville = value;
    }

    updatedDestinations[index] = destination;
    setDestinations(updatedDestinations);
  };

  const addDestination = () => {
    setDestinations([
      ...destinations,
      { pays: 4, wilaya: '', daira: '', ville: '' }
    ]);
  };

  const removeDestination = (index) => {
    const updatedDestinations = destinations.filter((_, i) => i !== index);
    setDestinations(updatedDestinations);
  };

  const isSpecialTransportType = (transport) => {
    return ['taxi', 'yassir', 'careem', 'avion', 'bus', 'train', 'bateau'].includes(transport?.type?.toLowerCase());
  };

  const handleClose = () => {
    formik.resetForm();
    setDestinations([]);
    setConfirmationData(null);
    setIsMissionCreated(false);
    setErrorMessage(null);
    setIsCreating(false);
    onClose();
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Créer une nouvelle mission</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {loading ? (
            <LinearProgress />
          ) : !confirmationData ? (
            <form onSubmit={formik.handleSubmit}>
              {/* Employee Selector */}
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Employé</InputLabel>
                <Select
                  name="employeeId"
                  value={userRole?.toString() === "1" || userRole?.toString() === "5" || userRole === 'admin-tenes' ? formik.values.employeeId : (employee?.id || '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                  disabled={isFormDisabled}
                >
                  {(userRole?.toString() === "1" || userRole?.toString() === "5" || userRole === 'admin-tenes') ? (
                    employees.length > 0 ? (
                      employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {`${emp.first_name} ${emp.last_name}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        Chargement des employés...
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem value={employee?.id || ''}>
                      {employee ? `${employee.first_name} ${employee.last_name}` : 'Loading...'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Order Number (Read-only) */}
              <TextField
                fullWidth
                margin="dense"
                label="Numéro d'ordre"
                value={nextOrderNumber || 'Chargement...'}
                disabled
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">📋</InputAdornment>,
                  style: { backgroundColor: '#f5f5f5', fontWeight: 'bold' }
                }}
                helperText="Ce numéro sera automatiquement assigné et incrémenté"
              />

              {/* Departure Location */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Lieu de départ</Typography>
              
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Pays de départ</InputLabel>
                <Select
                  name="selectedPays"
                  value={selectedPays}
                  onChange={(e) => setSelectedPays(e.target.value)}
                  disabled={isFormDisabled}
                >
                  {paysList.map(pays => (
                    <MenuItem key={pays.id} value={pays.id}>
                      {pays.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedPays === 4 && (
                <>
                  <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                    <InputLabel>Wilaya de départ</InputLabel>
                    <Select
                      name="wilaya"
                      value={selectedWilaya}
                      onChange={(e) => setSelectedWilaya(e.target.value)}
                      label="Wilaya"
                      disabled={isFormDisabled}
                    >
                      {algeriaWilayas.map((wilaya) => (
                        <MenuItem key={wilaya.id} value={wilaya.id}>
                          {wilaya.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    margin="dense"
                    label="Daïra de départ"
                    value={selectedDaira}
                    onChange={(e) => setSelectedDaira(e.target.value)}
                    disabled={isFormDisabled}
                  />
                </>
              )}

              {/* Reason */}
              <TextField
                fullWidth
                margin="dense"
                name="reason"
                label="Motif"
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={isFormDisabled}
                multiline
                rows={2}
              />

              {/* Dates */}
              <TextField
                fullWidth
                margin="dense"
                type="date"
                name="validityFrom"
                label="Date de début"
                InputLabelProps={{ shrink: true }}
                value={formik.values.validityFrom}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.validityFrom && Boolean(formik.errors.validityFrom)}
                helperText={formik.touched.validityFrom && formik.errors.validityFrom}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                disabled={isFormDisabled}
              />

              <TextField
                fullWidth
                margin="dense"
                type="date"
                name="validityTo"
                label="Date de fin"
                InputLabelProps={{ shrink: true }}
                value={formik.values.validityTo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.validityTo && Boolean(formik.errors.validityTo)}
                helperText={formik.touched.validityTo && formik.errors.validityTo}
                inputProps={{ min: formik.values.validityFrom }}
                disabled={isFormDisabled}
              />

              {/* Transport */}
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Moyen de Transport</InputLabel>
                <Select
                  name="transportId"
                  value={formik.values.transportId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.transportId && Boolean(formik.errors.transportId)}
                  disabled={isFormDisabled}
                >
                  {transports.map((transport) => (
                    <MenuItem key={transport.id} value={transport.id}>
                      {isSpecialTransportType(transport)
                        ? transport.type
                        : `${transport.brand} ${transport.model} (${transport.registration})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Billet for special transport types */}
              {transports.find(t => t.id === formik.values.transportId)?.type &&
                ['AVION', 'BUS', 'BATEAU', 'TRAIN'].includes(
                  transports.find(t => t.id === formik.values.transportId)?.type?.toUpperCase()
                ) && (
                  <TextField
                    fullWidth
                    margin="dense"
                    name="billet"
                    label="Billet"
                    value={formik.values.billet || ''}
                    onChange={formik.handleChange}
                    disabled={isFormDisabled}
                  />
                )}

              {/* Destinations */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Destinations</Typography>
              {destinations.map((destination, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Destination {index + 1}</Typography>

                  <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                    <InputLabel>Pays</InputLabel>
                    <Select
                      value={destination.pays}
                      onChange={(e) => handleDestinationChange(index, 'pays', e.target.value)}
                      disabled={isFormDisabled}
                    >
                      {paysList.map(pays => (
                        <MenuItem key={pays.id} value={pays.id}>
                          {pays.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {destination.pays === 4 ? (
                    <>
                      <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                        <InputLabel>Wilaya</InputLabel>
                        <Select
                          value={destination.wilaya}
                          onChange={(e) => handleDestinationChange(index, 'wilaya', e.target.value)}
                          disabled={isFormDisabled}
                        >
                          {algeriaWilayas.map((wilaya) => (
                            <MenuItem key={wilaya.id} value={wilaya.id}>
                              {wilaya.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        margin="dense"
                        label="Daïra"
                        value={destination.daira}
                        onChange={(e) => handleDestinationChange(index, 'daira', e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Ville"
                      value={destination.ville || ''}
                      onChange={(e) => handleDestinationChange(index, 'ville', e.target.value)}
                      disabled={isFormDisabled}
                    />
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => removeDestination(index)}
                    sx={{ mt: 1 }}
                    disabled={isFormDisabled}
                  >
                    Supprimer
                  </Button>
                </Box>
              ))}

              <Button
                variant="outlined"
                color="primary"
                onClick={addDestination}
                sx={{ mt: 1, mb: 2 }}
                disabled={isFormDisabled}
              >
                Ajouter une destination
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isFormDisabled}
                >
                  Créer
                </Button>
              </Box>
            </form>
          ) : (
            <Box>
              <Typography variant="h6">Confirmation de création</Typography>
              
              {/* Afficher le message d'erreur s'il y en a */}
              {errorMessage && (
                <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                  {errorMessage}
                </Typography>
              )}
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Numéro d'ordre :</Typography>
              <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {nextOrderNumber || '............................'}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Employé :</Typography>
              <Typography>
                {employees.find(e => e.id === confirmationData.employeeId)?.last_name}{' '}
                {employees.find(e => e.id === confirmationData.employeeId)?.first_name}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Dates :</Typography>
              <Typography>
                Du {confirmationData.validityFrom} au {confirmationData.validityTo}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Motif :</Typography>
              <Typography>{confirmationData.reason}</Typography>

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Transport :</Typography>
              {(() => {
                const transport = transports.find(t => t.id === confirmationData.transportId);
                return (
                  <Typography>
                    {isSpecialTransportType(transport)
                      ? transport.type
                      : `${transport.brand} ${transport.model} (${transport.registration})`}
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {confirmationData ? (
          <>
            <Button onClick={handleCancel} color="secondary" disabled={isCreating}>
              Annuler
            </Button>
            <Button 
              onClick={handleConfirm} 
              color="primary" 
              variant="contained"
              disabled={isCreating}
            >
              {isCreating ? 'Création en cours...' : 'Confirmer'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} color="secondary">
            Fermer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MissionForm;

